import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import ListenTextComponent from '../../components/ListenTextComponent';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faShare, faSave, faPrint } from '@fortawesome/free-solid-svg-icons';

const MyLearningNotes = () => {
  const authState = useSelector(state => state.authReducer);
  const [userId, setUserId] = useState(authState.user._id);
  const [notes, setNotes] = useState([]);
  const [category, setCategory] = useState('');
  const [myBook, setMyBook] = useState('');
  const [bookTitle, setBookTitle] = useState('');
  const [myBooks, setMyBooks] = useState([]);
  const [numberOfBooks, setNumberOfBooks] = useState([]);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [fetchData, { loading }] = useFetch();
  const [selectedRecordId, setSelectedRecordId] = useState(null);

  const fetchMyBooks = useCallback(() => {
    const config = {
        url: "/mybooks/forlearningnotes",
        method: "post",
        headers: { Authorization: authState.token },
        data: {userId: userId}
    };

    fetchData(config, { showSuccessToast: false })
        .then(response => {
          // console.log("Response mybooks: ",response.mybooks);
            if (response && Array.isArray(response.mybooks)) {
                setMyBooks(response.mybooks);
                setNumberOfBooks(myBooks.length);
            } else {
                console.error("Invalid response format: Book not found in response");
            }
        })
        .catch(error => {
            console.error("Error fetching MyBooks:", error);
        });
}, [authState.token, fetchData]);


useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchMyBooks();
}, [authState.isLoggedIn, fetchMyBooks]);

const sortedBooks = myBooks.sort((a, b) => {
  const bookTitleA = a.bookTitle.toLowerCase();
  const bookTitleB = b.bookTitle.toLowerCase();
  return bookTitleA.localeCompare(bookTitleB);
});

  const categories = [
    { label: 'Quick Note', value: 'Quick Note / Comment (Default)' },
    { label: 'Study Notes', value: 'Study Notes' },
    { label: 'Notes for Exam', value: 'Notes for Exam' },
    { label: 'Page Summary', value: 'Page Summary' },

    { label: 'Audio Notes', value: 'Audio Notes' },
    { label: 'Formula', value: 'Formula' },

    { label: 'Assignment', value: 'Assignment' },
    { label: 'Project', value: 'Project' },
    { label: 'Query', value: 'Query' },
    { label: 'Reference', value: 'Reference' },
    { label: 'Index / Keywords', value: 'Index / Keywords' },
    { label: 'Book Summary', value: 'Book Summary' },

    { label: 'Past Questions', value: 'Past Questions' },
    { label: 'My Question - Comprehension', value: 'My Question - Comprehension' },
    { label: 'My Question - Fill Up The Blanks', value: 'My Question - Fill Up The Blanks' },
    { label: 'My Question - Long Answer Type', value: 'My Question - Long Answer Type' },
    { label: 'My Question - Matching', value: 'My Question - Matching' },
    { label: 'My Question - MCQ', value: 'My Question - MCQ' },
    { label: 'My Question - Non-Numericals', value: 'My Question - Non-Numericals' },
    { label: 'My Question - Numericals', value: 'My Question - Numericals' },
    { label: 'My Question - Short Answer Type', value: 'My Question - Short Answer Type' },
    { label: 'My Question - True/False', value: 'My Question - True/False' },

    { label: 'Image', value: 'Image' },
    { label: 'Video', value: 'Video' },
    { label: 'TODO Action Items', value: 'TODO Action Items' },

    { label: 'Appreciation', value: 'Appreciation' },
  ];

  //Get Notes Data
  const fetchNotesByType = useCallback(() => {
    const config = {
      url: "/myreadingnotes/forlearningnotes",
      method: "post",
      headers: { Authorization: authState.token },
      data: {
        bookId: myBook, 
        userId: userId, 
        notesType: category
      }
    };
    // console.log("Read Comments config to server: ", config);
    
    fetchData(config, { showSuccessToast: false })
      .then(response => {
        // console.log("Read Comments Response:", response);
        if (response && response.status) {
          setNotes(response.notes);
          // console.log("setNotes ", response.notes); 
        } else {
          console.error("Notes data not found in response");
        }
      })
      .catch(error => {
        console.error("Error fetching Notes data:", error);
      });
  }, [authState.token, fetchData, myBook, category, userId]);


  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchNotesByType();
  }, [authState.isLoggedIn, fetchNotesByType]);

  const sortedNotes = [...notes ].sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

  const handleSearch = () => {
    if (!category) {
      setNotificationMessage('Please select a category before searching.');
    } else {
      fetchNotesByType();
    }
  };

  const handleView = (recordID) => {
    setSelectedRecordId(prevId => (prevId === recordID ? null : recordID));
  };

  const handleRecordShare = (recordID) => {
    setSelectedRecordId(recordID);
  };

  const handleRecordSave = (recordID) => {
    setSelectedRecordId(recordID);
  };

  const handleRecordPrint = (recordID) => {
    setSelectedRecordId(recordID);
  };

  const formatDateTime = (date) => {
    const options = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
    };
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

  return (
    <div className="min-h-screen bg-black py-2">
      <div className='border-b border-blue-400 mb-2'>
        <h2 className="text-lg lg:text-xl font-extrabold text-center text-yellow-200 mb-2 p-2">
          📖 Learnings Notes
        </h2>
      </div>

      <div className="my-2 mx-auto py-4">
          <div className="flex justify-left">
              <p className='text-left text-sm lg:text-m text-yellow-200 px-4'>Select Book & Type To View Your Content</p>
          </div>
          <div className="flex flex-col justify-center ml-2 mr-2 border-b border-blue-400 p-2">
            <div className="search-row mt-2">
              <select
                className="h-10 w-full rounded-md"
                value={myBook}
                onChange={(e) => {
                  const selectedBookId = e.target.value;
                  const selectedBook = sortedBooks.find(book => book.bookId === selectedBookId);
                  setMyBook(selectedBookId);
                  setBookTitle(selectedBook.bookTitle);
                }}
              >
                {sortedBooks.map((book, index) => (
                  <option key={index} value={book.bookId}>
                    {book.bookTitle}
                  </option>
                ))}
              </select>
            </div>
            <div className="search-row mt-2">
              <div className="input-container">
                <select className="h-10 rounded-md" value={category} onChange={(e) => setCategory(e.target.value)}>
                  {categories.map((cat, index) => (
                    <option key={index} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="search-row mt-2 ml-4">
              <button className="small-button" onClick={handleSearch} style={{ width: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>
          <div className="p-2 ml-2 mr-2 mt-2">
            <div className="flex justify-between items-center">
              <div>
                {notes.length !== 0 && 
                  <h2 className='my-2 ml-2 md:ml-0 text-white'>Search Result for {category} in {bookTitle}
                </h2>}
              </div>
            </div>
            {loading ? (
              <Loader />
            ) : (
              <div className='h-1/2'>
                {notes.length === 0 ? (
                  <div className='flex items-center justify-center gap-4 text-yellow-300'>
                    <span>No record found</span>
                  </div>
                ) : (
                  sortedNotes.map((record, index) => (
                    <div key={record._id} className='bg-black border border-gray-400 my-2 p-2 text-white rounded-md shadow-md'>
                      <div className='flex'>
                        <span className='font-medium'>#{index + 1}. {record.notesTitle}</span>
                        <Tooltip text={"View details"} position={"top"}>
                          <span className='ml-auto mr-4 text-blue-500 cursor-pointer' onClick={() => handleView(record._id)}>
                            <FontAwesomeIcon icon={faEye} />
                          </span>
                        </Tooltip>
                      </div>
                      {selectedRecordId === record._id && (
                        <div className='bg-black border border-blue-500  text-white p-2 text-sm lg:text-m'>
                          <div className='flex flex-wrap text-yellow-200'>
                            {bookTitle}{': '}
                            {record.notesType === '' ? "Undefined" : record.notesType}
                          </div>
                          <div className='flex flex-wrap'><span className='text-gray-300'>Title#: </span>{record.notesTitle}</div>
                          <div className='flex flex-wrap mt-4 mb-4 border-sky-200'><span className='text-gray-300'>Details: </span>{record.notesText}</div>
                          {record.notesType === 'Audio Notes' && (
                            <ListenTextComponent param={record.notesText}/>
                          )}
                          {record.notesRemarks !== '' && (
                              <div className='flex flex-wrap'><span className='text-gray-300'>Remarks: </span>{record.notesRemarks}</div>
                          )}
                          <div className='flex flex-wrap text-gray-200'><span className='text-gray-300'>Page#: </span>{record.pageNumber}</div>
                          <div className='flex flex-wrap text-gray-200'><span className='text-gray-300'>Notes By: </span>{record.notesBy}</div>
                          <div className='flex flex-wrap'><span className='text-gray-300'>Date#: </span>{formatDateTime(record.dateCreated)}</div>

                          <Tooltip text={"Share"} position={"top"}>
                            <span className='text-green-500 cursor-pointer mr-4' onClick={() => handleRecordShare(record._id)}>
                              <FontAwesomeIcon icon={faShare} />
                            </span>
                          </Tooltip>
                          <Tooltip text={"Save"} position={"top"}>
                            <span className='text-blue-500 cursor-pointer mr-4' onClick={() => handleRecordSave(record._id)}>
                              <FontAwesomeIcon icon={faSave} />
                            </span>
                          </Tooltip>
                          <Tooltip text={"Print"} position={"top"}>
                            <span className='text-orange-500 cursor-pointer mr-4' onClick={() => handleRecordPrint(record._id)}>
                              <FontAwesomeIcon icon={faPrint} />
                            </span>
                          </Tooltip>
                        </div>
                      )}

                    </div>
                  ))
                )}
              </div>
            )}
          </div>
      </div>
      <footer className="bg-black border-t border-blue-400 text-gray-400 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>  
    </div>
  );
};

export default MyLearningNotes;
